/**
 * ArticleImageMenu
 * Bubble menu for the Article Image selected in RichTextEditor: edits its
 * Image Caption, Image Placement and Image Size. Clicking an image selects it
 * (a ProseMirror node selection); the menu follows that selection and floats
 * just below the image, or above it when there is no room below.
 *
 * Built on the editor's own state rather than @tiptap/extension-bubble-menu,
 * which is not a declared dependency of this app. Replacing an image is
 * deliberately not offered: delete it and insert a new one.
 */
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useEditorState, type Editor } from '@tiptap/react';
import { NodeSelection } from '@tiptap/pm/state';
import { AlignCenter, AlignLeft, AlignRight, type LucideIcon } from 'lucide-react';
import clsx from 'clsx';
import type { ImagePlacement, ImageSize } from '@app-types';
import { getDefaultImageSize, IMAGE_PLACEMENTS, IMAGE_SIZES } from './articleImageHelpers';
import { ARTICLE_IMAGE_NODE, type ArticleImageAttributes } from './extensions/ArticleImage';
import styles from './ArticleImageMenu.module.css';

export interface ArticleImageMenuProps {
  editor: Editor | null;
  dataTestId: string;
}

interface SelectedImage {
  pos: number;
  caption: string;
  placement: ImagePlacement;
  size: ImageSize;
}

interface MenuPosition {
  top: number;
  left: number;
}

const CAPTION_MAX_LENGTH = 300;
const MENU_GAP = 8;

const PLACEMENT_OPTIONS: Record<ImagePlacement, { label: string; icon: LucideIcon }> = {
  left: { label: 'Left', icon: AlignLeft },
  center: { label: 'Center', icon: AlignCenter },
  right: { label: 'Right', icon: AlignRight }
};

const SIZE_LABELS: Record<ImageSize, string> = {
  small: 'Small',
  medium: 'Medium',
  full: 'Full'
};

const ArticleImageMenu: React.FC<ArticleImageMenuProps> = ({ editor, dataTestId }) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<MenuPosition | null>(null);

  const selectedImage = useEditorState({
    editor,
    selector: ({ editor: currentEditor }): SelectedImage | null => {
      const selection = currentEditor?.state.selection;
      if (!(selection instanceof NodeSelection) || selection.node.type.name !== ARTICLE_IMAGE_NODE) return null;
      const attrs = selection.node.attrs as ArticleImageAttributes;
      return { pos: selection.from, caption: attrs.caption, placement: attrs.placement, size: attrs.size };
    }
  });

  /**
   * Places the menu relative to its positioned container (the editor box):
   * below the image when it fits inside the box, otherwise above it, and
   * clamped horizontally so it never leaves the box.
   */
  const updatePosition = useCallback(() => {
    const menu = menuRef.current;
    const container = menu?.offsetParent;
    if (!editor || !menu || !container || !selectedImage) return;

    const imageDom = editor.view.nodeDOM(selectedImage.pos);
    if (!(imageDom instanceof HTMLElement)) return;

    const imageRect = imageDom.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const menuHeight = menu.offsetHeight;
    const menuWidth = menu.offsetWidth;

    const below = imageRect.bottom - containerRect.top + MENU_GAP;
    const above = imageRect.top - containerRect.top - menuHeight - MENU_GAP;
    const fitsBelow = below + menuHeight <= containerRect.height;
    const top = fitsBelow || above < 0 ? Math.min(below, containerRect.height - menuHeight) : above;

    const maxLeft = Math.max(containerRect.width - menuWidth - MENU_GAP, MENU_GAP);
    const left = Math.min(Math.max(imageRect.left - containerRect.left, MENU_GAP), maxLeft);

    setPosition({ top: Math.max(top, 0), left });
  }, [editor, selectedImage]);

  useLayoutEffect(() => {
    if (!editor || !selectedImage) {
      setPosition(null);
      return undefined;
    }

    updatePosition();
    // The image moves when the editor's content area scrolls (scroll does not
    // bubble, hence the capture listener), with layout changes from edits, and
    // when its picture finishes loading and takes its real height.
    const editorDom = editor.view.dom;
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    editorDom.addEventListener('load', updatePosition, true);
    editor.on('transaction', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
      editorDom.removeEventListener('load', updatePosition, true);
      editor.off('transaction', updatePosition);
    };
  }, [editor, selectedImage, updatePosition]);

  if (!editor || !selectedImage) return null;

  const updateImage = (attributes: Partial<ArticleImageAttributes>) => {
    editor.commands.updateAttributes(ARTICLE_IMAGE_NODE, attributes);
  };

  /**
   * A Size still at the old Placement's default follows the new Placement's
   * default (Center -> Left goes Full -> Medium so text can wrap); a Size the
   * Admin chose explicitly is kept.
   */
  const handlePlacementChange = (placement: ImagePlacement) => {
    const sizeIsDefault = selectedImage.size === getDefaultImageSize(selectedImage.placement);
    updateImage(sizeIsDefault ? { placement, size: getDefaultImageSize(placement) } : { placement });
  };

  const handleCaptionKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Enter would otherwise submit the surrounding article form.
    if (event.key === 'Enter' || event.key === 'Escape') {
      event.preventDefault();
      editor.commands.focus();
    }
  };

  return (
    <div
      ref={menuRef}
      className={styles.menu}
      style={position ? { top: position.top, left: position.left } : { visibility: 'hidden' }}
      role="toolbar"
      aria-label="Image options"
      data-test-id={`${dataTestId}-image-menu`}
    >
      <input
        type="text"
        className={styles.captionInput}
        value={selectedImage.caption}
        onChange={(event) => updateImage({ caption: event.target.value })}
        onKeyDown={handleCaptionKeyDown}
        placeholder="Add a caption"
        aria-label="Image caption"
        maxLength={CAPTION_MAX_LENGTH}
        data-test-id={`${dataTestId}-image-caption`}
      />

      <span className={styles.divider} aria-hidden="true" />

      <div className={styles.group} role="group" aria-label="Image placement">
        {IMAGE_PLACEMENTS.map((placement) => {
          const { label, icon: Icon } = PLACEMENT_OPTIONS[placement];
          const isActive = selectedImage.placement === placement;
          return (
            <button
              key={placement}
              type="button"
              className={clsx(styles.menuButton, isActive && styles.menuButtonActive)}
              // Keep the image selected (see ToolbarButton in RichTextEditor).
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => handlePlacementChange(placement)}
              title={`Place ${label.toLowerCase()}`}
              aria-label={`Place ${label.toLowerCase()}`}
              aria-pressed={isActive}
              data-test-id={`${dataTestId}-image-placement-${placement}`}
            >
              <Icon size={16} />
            </button>
          );
        })}
      </div>

      <span className={styles.divider} aria-hidden="true" />

      <div className={styles.group} role="group" aria-label="Image size">
        {IMAGE_SIZES.map((size) => {
          const isActive = selectedImage.size === size;
          return (
            <button
              key={size}
              type="button"
              className={clsx(styles.menuButton, styles.menuButtonText, isActive && styles.menuButtonActive)}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => updateImage({ size })}
              aria-pressed={isActive}
              data-test-id={`${dataTestId}-image-size-${size}`}
            >
              {SIZE_LABELS[size]}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ArticleImageMenu;
